import { useState, useEffect } from "react";
import {
  ClockIcon,
  MapPinIcon,
} from "@heroicons/react/20/solid";
import {
  EllipsisVerticalIcon,
} from "@heroicons/react/20/solid";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { Fragment } from "react";
import { DateTime } from "luxon";
import StatusIndicator from "../Shared/StatusIndicator";
import PrimaryButton from "../Shared/PrimaryButton";
import AppointmentTypeBadge from "../Shared/AppointmentTypeBadge";
import { APPOINTMENT_STATUS, APPOINTMENT_TYPES } from "../../config/appointment";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import { formatPriceWithCurrency } from "../../utility/priceUtils";
import { parseDecimal128 } from "../../utility/priceUtils";
import { getOriginalTimeDisplay } from "../../utility/timezoneUtils";

const NextAppointmentCard = ({
  todayAppointments = [],
  isLoading,
  currency,
  onView,
  onEdit,
  onCancel,
  onContact,
  onUpdateStatus,
  onClientClick,
}) => {
  const { is12HourClock } = useUserPreferences();
  const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [now, setNow] = useState(DateTime.now());

  // Update "now" every minute for countdown
  useEffect(() => {
    const interval = setInterval(() => setNow(DateTime.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div className="bg-white rounded-card shadow-hairline border border-ink-100 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-5 w-32 bg-cream-300 rounded"></div>
          <div className="h-8 w-64 bg-cream-300 rounded"></div>
          <div className="h-4 w-48 bg-cream-300 rounded"></div>
        </div>
      </div>
    );
  }

  const activeAppointments = todayAppointments.filter(
    (a) => a.status === "confirmed" || a.status === "pending"
  );
  const completedToday = todayAppointments.filter(
    (a) => a.status === "completed"
  );

  // Find next upcoming appointment (confirmed, start time in the future)
  const nextAppointment = activeAppointments
    .filter((a) => {
      const start = DateTime.fromISO(a.startTime);
      return start > now && a.status === "confirmed";
    })
    .sort((a, b) => DateTime.fromISO(a.startTime) - DateTime.fromISO(b.startTime))[0];

  // Currently happening (confirmed, started but not ended)
  const currentAppointment = !nextAppointment
    ? activeAppointments.find((a) => {
        const start = DateTime.fromISO(a.startTime);
        const end = DateTime.fromISO(a.endTime);
        return start <= now && end > now && a.status === "confirmed";
      })
    : null;

  const heroAppointment = nextAppointment || currentAppointment;

  // Compute today's revenue from completed appointments
  const todayRevenue = completedToday.reduce((sum, a) => {
    return sum + (parseDecimal128(a.totalPrice) || 0);
  }, 0);

  const formatTime = (isoTime) => {
    if (!isoTime) return "";
    const format = is12HourClock ? "h:mm a" : "HH:mm";
    return DateTime.fromISO(isoTime).setZone(systemTimezone).toFormat(format);
  };

  const getOriginalTimeDisplayForAppointment = (appointment) => {
    if (
      !appointment?.timeZoneId ||
      appointment.timeZoneId === systemTimezone ||
      !appointment.startTime
    ) {
      return null;
    }
    return getOriginalTimeDisplay(
      appointment.startTime,
      appointment.timeZoneId,
      is12HourClock
    );
  };

  const getCountdown = (isoTime) => {
    const start = DateTime.fromISO(isoTime);
    const diff = start.diff(now, ["hours", "minutes"]);
    const hours = Math.floor(diff.hours);
    const minutes = Math.floor(diff.minutes);

    if (hours > 0) {
      return `in ${hours}h ${minutes > 0 ? `${minutes}m` : ""}`;
    }
    if (minutes > 0) {
      return `in ${minutes}m`;
    }
    return "starting now";
  };

  const formatServicesSummary = (appointment) => {
    if (!appointment.services || appointment.services.length === 0) return "";
    const names = appointment.services.map((s) => s.name);
    const totalDuration = appointment.services.reduce(
      (sum, s) => sum + (s.duration || 0) * (s.quantity || 1),
      0
    );
    const durationStr =
      totalDuration >= 60
        ? `${Math.floor(totalDuration / 60)}h${totalDuration % 60 > 0 ? ` ${totalDuration % 60}m` : ""}`
        : `${totalDuration}m`;

    if (names.length <= 2) {
      return `${names.join(" + ")} — ${durationStr}`;
    }
    return `${names[0]} + ${names.length - 1} more — ${durationStr}`;
  };

  const getFormattedAppointment = (appointment) => {
    const status = APPOINTMENT_STATUS.find((x) => x.id === appointment.status);
    return {
      ...appointment,
      id: appointment.id,
      startTime: formatTime(appointment.startTime),
      endTime: formatTime(appointment.endTime),
      status: status?.name,
      statusColour: status?.colour,
      address: appointment.serviceAddress?.full,
    };
  };

  const isMobile = heroAppointment?.type === APPOINTMENT_TYPES.MOBILE;

  // State: All done for today
  if (!heroAppointment && completedToday.length > 0) {
    return (
      <div className="bg-white rounded-card shadow-hairline border border-ink-100 p-6">
        <p className="smallcaps text-ink-400 mb-2">Day Complete</p>
        <p className="font-display text-2xl text-ink-900">
          {completedToday.length} appointment{completedToday.length !== 1 ? "s" : ""} completed
        </p>
        {todayRevenue > 0 && (
          <p className="text-sm text-ink-500 mt-1 tabular">
            {formatPriceWithCurrency(todayRevenue, currency)} earned today
          </p>
        )}
      </div>
    );
  }

  // Note: the "no appointments today" empty state is owned by DashboardEmptyState
  // (rendered by DashboardPage when today has no appointments), so this component
  // is only mounted when todayAppointments is non-empty.

  // State: No more upcoming (but there are non-completed appointments like cancelled/pending)
  if (!heroAppointment) {
    return (
      <div className="bg-white rounded-card shadow-hairline border border-ink-100 p-6">
        <p className="smallcaps text-ink-400 mb-2">Next Up</p>
        <p className="font-display text-2xl text-ink-900">
          No more appointments today
        </p>
        {todayRevenue > 0 && (
          <p className="text-sm text-ink-500 mt-1 tabular">
            {formatPriceWithCurrency(todayRevenue, currency)} earned today
          </p>
        )}
      </div>
    );
  }

  // State: Active appointment to show
  const formatted = getFormattedAppointment(heroAppointment);
  const isHappening = currentAppointment && !nextAppointment;
  const originalTimeDisplay = getOriginalTimeDisplayForAppointment(heroAppointment);

  return (
    <div className="bg-white rounded-card shadow-hairline border border-ink-100 p-6 sm:p-7">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-x-3">
          <span className="smallcaps text-ink-400">
            {isHappening ? "Happening Now" : "Next Up"}
          </span>
          <StatusIndicator
            variant="dot"
            hideWhenConfirmed
            colour={formatted.statusColour}
            label={formatted.status}
          />
          <AppointmentTypeBadge type={heroAppointment.type} size="sm" />
        </div>

        {/* Desktop menu */}
        <div className="flex items-center gap-x-2">
          <Menu as="div" className="relative flex-none">
            <MenuButton className="-m-2.5 block p-2.5 text-ink-500 hover:text-ink-900">
              <span className="sr-only">Open options</span>
              <EllipsisVerticalIcon aria-hidden="true" className="size-5" />
            </MenuButton>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems
                anchor="bottom end"
                className="z-10 w-36 rounded-md bg-white py-2 shadow-lg ring-1 ring-ink-100 focus:outline-none"
              >
                <MenuItem>
                  {({ close }) => (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        onView({
                          ...formatted,
                          clientName: heroAppointment.clientFullName,
                        });
                        close();
                      }}
                      className="block px-3 py-1 text-sm/6 text-ink-900 data-[focus]:bg-cream-100 data-[focus]:outline-none"
                    >
                      View Details
                    </a>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ close }) => (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        onEdit(heroAppointment);
                        close();
                      }}
                      className="block px-3 py-1 text-sm/6 text-ink-900 data-[focus]:bg-cream-100 data-[focus]:outline-none"
                    >
                      Edit
                    </a>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ close }) => (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        onContact(heroAppointment);
                        close();
                      }}
                      className="block px-3 py-1 text-sm/6 text-ink-900 data-[focus]:bg-cream-100 data-[focus]:outline-none"
                    >
                      Contact
                    </a>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ close }) => (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        onCancel(heroAppointment);
                        close();
                      }}
                      className="block px-3 py-1 text-sm/6 text-signal-red-600 data-[focus]:bg-cream-100 data-[focus]:outline-none"
                    >
                      Cancel
                    </a>
                  )}
                </MenuItem>
              </MenuItems>
            </Transition>
          </Menu>
        </div>
      </div>

      {/* Editorial hero layout: time on left, details on right */}
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-x-8 mb-4">
        <div className="shrink-0">
          <p className="font-display text-4xl sm:text-5xl text-ink-900 tabular tracking-display">
            {formatted.startTime}
          </p>
          {!isHappening && (
            <p className="text-xs text-ink-400 mt-1 tabular">
              {getCountdown(heroAppointment.startTime)}
            </p>
          )}
          {originalTimeDisplay && (
            <p className="text-[11px] text-ink-500 mt-0.5">
              {originalTimeDisplay}
            </p>
          )}
        </div>

        <div className="hidden sm:block h-12 w-px bg-ink-100" aria-hidden="true" />

        <div className="mt-3 sm:mt-0 min-w-0 flex-1">
          <button
            onClick={() => onClientClick(heroAppointment.clientId)}
            className="font-display text-xl text-ink-900 hover:text-accent-600 transition-colors text-left truncate block"
          >
            {heroAppointment.clientFullName}
          </button>
          {heroAppointment.services && heroAppointment.services.length > 0 && (
            <p className="text-sm text-ink-500 mt-0.5">
              {formatServicesSummary(heroAppointment)}
            </p>
          )}
        </div>
      </div>

      {/* Location row */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-6 text-sm text-ink-600">
        <div className="sm:hidden">
          <div className="flex items-center gap-x-2">
            <ClockIcon className="size-4 text-ink-400 flex-shrink-0" />
            <span className="font-medium tabular">{formatted.startTime}</span>
          </div>
        </div>

        {heroAppointment.serviceAddress?.full && (
          <div className="flex items-center gap-x-2 min-w-0">
            <MapPinIcon className="size-4 text-ink-400 flex-shrink-0" />
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                heroAppointment.serviceAddress.full
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-ink-600 hover:text-ink-800 hover:underline truncate"
              title={heroAppointment.serviceAddress.full}
            >
              {heroAppointment.serviceAddress.full}
            </a>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mt-4">
        {isMobile && heroAppointment.serviceAddress?.full && (
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
              heroAppointment.serviceAddress.full
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-pill bg-accent-500 px-4 py-2 text-center text-sm font-medium text-cream-50 hover:bg-accent-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500 transition-colors"
          >
            Get Directions
          </a>
        )}
        {heroAppointment.status === "confirmed" && (
          <PrimaryButton
            colour="light-green"
            onClick={() => onUpdateStatus(heroAppointment.id, "completed")}
            className="text-sm"
          >
            Complete
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};

export default NextAppointmentCard;
